if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        // Mobile swipes also switch the video that may be played.
        this.playActiveMedia(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        if (window.OliviaProductVideos) window.OliviaProductVideos.activate(activeItem);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

// Olivia product gallery video controls: prefer sound, with a play button when
// browsers block autoplay with audio. This does not affect non-product videos.
if (!window.__oliviaProductSoundPlayerInitialized) {
  window.__oliviaProductSoundPlayerInitialized = true;
  (() => {
    const selector = 'media-gallery .product__media-list .olivia-product-video video';

    function getButton(video) {
      return video.closest('.olivia-product-video')?.querySelector('.olivia-video-play-sound');
    }

    function showPlayButton(video, visible) {
      const button = getButton(video);
      if (button) button.hidden = !visible;
    }

    function makeUnmuted(video) {
      video.controls = true;
      video.playsInline = true;
      video.defaultMuted = false;
      video.muted = false;
      video.removeAttribute('muted');
    }

    function pauseOtherVideos(activeVideo) {
      document.querySelectorAll(selector).forEach((other) => {
        if (other !== activeVideo && !other.paused) other.pause();
      });
    }

    function playWithSound(video) {
      if (!video) return;
      makeUnmuted(video);
      pauseOtherVideos(video);
      let result;
      try {
        result = video.play();
      } catch (error) {
        showPlayButton(video, true);
        return;
      }
      if (result && typeof result.then === 'function') {
        result.then(() => showPlayButton(video, false))
          .catch(() => showPlayButton(video, video.paused));
      } else {
        showPlayButton(video, video.paused);
      }
    }

    function activate(activeItem) {
      if (!activeItem) return;
      const gallery = activeItem.closest('media-gallery');
      const activeVideo = activeItem.querySelector('.olivia-product-video video');
      if (gallery) {
        gallery.querySelectorAll('.product__media-list .olivia-product-video video').forEach((video) => {
          if (video !== activeVideo && !video.paused) video.pause();
        });
      }
      if (activeVideo && activeVideo.paused) playWithSound(activeVideo);
    }

    function initializeVideo(video) {
      if (video.dataset.oliviaSoundReady) return;
      video.dataset.oliviaSoundReady = 'true';
      video.autoplay = false;
      makeUnmuted(video);
      video.addEventListener('play', () => {
        showPlayButton(video, false);
        pauseOtherVideos(video);
      });
    }

    function initializeGalleries() {
      document.querySelectorAll(selector).forEach(initializeVideo);
      document.querySelectorAll('media-gallery').forEach((gallery) => {
        const activeItem = gallery.querySelector('.product__media-list .product__media-item.is-active');
        if (activeItem && activeItem.querySelector('.olivia-product-video video')) activate(activeItem);
      });
    }

    document.addEventListener('click', (event) => {
      const playButton = event.target.closest('.olivia-video-play-sound');
      if (playButton) {
        event.preventDefault();
        event.stopPropagation();
        const video = playButton.closest('.olivia-product-video')?.querySelector('video');
        playWithSound(video); // user click allows sound on desktop and mobile
        return;
      }
      const video = event.target.closest(selector);
      if (video) makeUnmuted(video); // respect native play/pause/volume controls
    });

    window.OliviaProductVideos = { activate: activate, playWithSound: playWithSound };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeGalleries);
    } else {
      initializeGalleries();
    }
    document.addEventListener('shopify:section:load', initializeGalleries);
  })();
}
